import { Api } from '@monorepo/shared/apiClient';
import { Loading } from '@monorepo/shared/components/Loading';
import { EmptyState } from '@monorepo/shared/componentsV2/EmptyState';
import { Main } from '@monorepo/shared/componentsV2/Main';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { Pagination } from '@monorepo/shared/componentsV2/Table/Pagination';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import { TableColumn } from '@monorepo/shared/componentsV2/Table/types';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import { useFormTemplatesList } from '@monorepo/shared/hooks/formTemplates/useFormTemplatesList';
import {
  useHasOrganizationUpdatePermissions,
  useHasProjectSettingsPermissionForAnyProjectInOrg,
} from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useCurrentUser } from '@monorepo/shared/hooks/useCurrentUser';
import { useFrontendPagination } from '@monorepo/shared/hooks/useFrontendPagination';
import { focusOutline } from '@monorepo/shared/styles/focus';
import {
  fillHeightAndScrollable,
  verticalLayout,
} from '@monorepo/shared/styles/layout';
import { getUserDisplayName } from '@monorepo/shared/utils/getUserDisplayName';
import { lookFor } from '@monorepo/shared/utils/lookFor';
import LockIcon from '@svg/lock.svg';
import { format } from 'date-fns';
import {
  FormTemplateRefResponse,
  getCategoryDisplayString,
} from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useOrgSpecificNewFormLink } from '../../hooks/useFormLinks';
import { ViewFormsTableActions } from './ViewFormsTableActions';

const StyledMain = styled(Main)`
  ${verticalLayout}
  ${fillHeightAndScrollable}
`;

const StyledTable = styled(Table)`
  height: 100%;
  padding-bottom: 4rem;
` as typeof Table;

// todo: would be nice to have a reusable + accessible "IconWithTooltip" component
const StyledIconContainer = styled.span`
  &:focus-visible {
    ${focusOutline({ $outlineOffset: '0' })}
  }

  svg {
    height: 1rem;
    margin-top: 0.25rem;
    color: ${({ theme }) => theme.colors.gray999};
  }
`;

const NameWithLock = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

export type FormTemplateRefDisplayRow = Omit<
  FormTemplateRefResponse,
  'category' | 'originalCreatedAt' | 'originalCreatedBy'
> & {
  category: string;
  originalCreatedAt: string;
  originalCreatedBy: string;
};

// Avoiding duplication on these conversions for the display + the search
function getDisplayRow(
  {
    category,
    originalCreatedAt,
    originalCreatedBy,
    ...formTemplateRow
  }: FormTemplateRefResponse,
  currentUser: Api.SnakeCasedUser | undefined,
): FormTemplateRefDisplayRow {
  return {
    ...formTemplateRow,
    category: category ? getCategoryDisplayString(category) : '',
    originalCreatedAt: originalCreatedAt
      ? format(new Date(originalCreatedAt), 'Pp')
      : '',
    originalCreatedBy: originalCreatedBy
      ? getUserDisplayName(originalCreatedBy, currentUser)
      : '',
  };
}

function getColumns({
  isAdminOnAnyProjectInOrg,
  lockedTooltipWarning,
}: {
  isAdminOnAnyProjectInOrg: boolean;
  lockedTooltipWarning: string;
}): TableColumn<FormTemplateRefDisplayRow>[] {
  const columns: TableColumn<FormTemplateRefDisplayRow>[] = [
    {
      id: 'name',
      header: 'Name',
      contents: ({ name, canBeEdited }) => (
        <NameWithLock>
          {isAdminOnAnyProjectInOrg && !canBeEdited && (
            <Tooltip title={lockedTooltipWarning}>
              {/** todo: not good accessibility, replace with better solution */}
              <StyledIconContainer tabIndex={0}>
                <LockIcon />
              </StyledIconContainer>
            </Tooltip>
          )}
          <span>{name}</span>
        </NameWithLock>
      ),
    },
    {
      id: 'category',
      header: 'Category',
      contents: 'category',
      align: 'center',
    },
    {
      id: 'originalCreatedAt',
      header: 'Created At',
      contents: 'originalCreatedAt',
    },
    {
      id: 'originalCreatedBy',
      header: 'Created By',
      contents: 'originalCreatedBy',
      width: '10%',
    },
  ];
  if (isAdminOnAnyProjectInOrg) {
    columns.push({
      id: 'actions',
      header: 'Actions',
      contents: (form) => <ViewFormsTableActions form={form} />,
      align: 'center',
      width: '10%',
    });
  }
  return columns;
}

interface ViewFormsTableProps {
  filter: string;
  organizationId: string;
}

export function ViewFormsTable({
  filter,
  organizationId,
}: ViewFormsTableProps) {
  const { formTemplateList, isLoading } = useFormTemplatesList({
    organizationId,
  });

  const { currentUser } = useCurrentUser();
  const hasOrgUpdatePermissions =
    useHasOrganizationUpdatePermissions(organizationId);
  const { isLoading: isLoadingPermissions, isAdminOnAnyProjectInOrg } =
    useHasProjectSettingsPermissionForAnyProjectInOrg(organizationId);

  const navigate = useNavigate();
  const newFormLink = useOrgSpecificNewFormLink();
  const onAddNew = React.useCallback(
    () => navigate(newFormLink),
    [navigate, newFormLink],
  );

  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();

  const columns = useMemo(() => {
    const lockedTooltipWarning = `This template is locked, but it can be unlocked. Please reach out to your ${
      !hasOrgUpdatePermissions ? 'organization admin, CSM,' : 'CSM'
    } or help@mapistry.com for assistance.`;
    return getColumns({
      isAdminOnAnyProjectInOrg,
      lockedTooltipWarning,
    });
  }, [hasOrgUpdatePermissions, isAdminOnAnyProjectInOrg]);

  const formTemplateDisplayRows: FormTemplateRefDisplayRow[] | undefined =
    formTemplateList
      ? formTemplateList.map((row) => getDisplayRow(row, currentUser))
      : undefined;

  const filterRowsBy = useCallback(
    (row: FormTemplateRefDisplayRow) =>
      lookFor(filter, row, ['name', 'category', 'originalCreatedBy']),
    [filter],
  );

  const { page, setPage, filteredResults, rows, rowsPerPage } =
    useFrontendPagination({
      items: formTemplateDisplayRows,
      filterRowsBy,
      resetPageWhen: [filter],
    });

  const aboutForms =
    'Forms are a flexible way to collect and track inspection data across multiple sites.';

  // todo@formTemplates eventually, site admins will be able to create forms, use isAdminOnAnyProjectInOrg then
  const secondaryText = hasOrgUpdatePermissions
    ? `Click to create a new form. ${aboutForms}`
    : `${aboutForms} Contact your organization administrator to configure a form.`;

  if (isLoading || isLoadingPermissions) {
    return <Loading />;
  }

  return (
    <StyledMain>
      {formTemplateDisplayRows?.length ? (
        <>
          <SkipLink linkText="Skip to pagination" />
          <StyledTable
            ariaLabel="Forms List"
            columns={columns}
            rows={rows}
            isPrefilteredAndNotEmpty
            makeTableFocusable
          />
          <SkipLinkTarget />
          <Pagination
            totalRowCount={filteredResults.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          actionButtonConfigs={
            hasOrgUpdatePermissions
              ? [{ label: 'Create new form', onClick: onAddNew }]
              : []
          }
          primaryText="There are no forms configured for this organization."
          secondaryText={secondaryText}
        />
      )}
    </StyledMain>
  );
}
